import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import api from '../utils/api';

const VendorInfoCard = () => {
    const [vendor, setVendor] = useState({});
    const [showModal, setShowModal] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        contactNumbers: {
            primary: {
                formatted: '',
                e164: ''
            },
            additional: []
        },
        email: '',
    });
    const [alert, setAlert] = useState({ message: '', variant: '' });

    useEffect(() => {
        fetchVendorData();
    }, []);

    const fetchVendorData = async () => {
        try {
            const response = await api.get('/vendors/profile');
            setVendor(response.data.data);
        } catch (error) {
            console.error('Error fetching vendor details:', error);
            setAlert({ message: 'Failed to load vendor details.', variant: 'danger' });
        }
    };

    // Stop propagation helper to prevent event bubbling
    const stopPropagation = (e) => {
        if (e && e.stopPropagation) {
            e.stopPropagation();
        }
    };

    const handleEdit = (e) => {
        // Stop propagation to prevent sidebar from closing
        stopPropagation(e);
        
        // Initialize form data properly handling both old and new schema
        let initialFormData = {
            firstName: vendor.firstName || '',
            lastName: vendor.lastName || '',
            email: vendor.email || '',
        };

        // Handle contact numbers based on schema (new or old)
        if (vendor.contactNumbers) {
            // New schema
            initialFormData.contactNumbers = {
                primary: {
                    formatted: vendor.contactNumbers.primary?.formatted || '',
                    e164: vendor.contactNumbers.primary?.e164 || ''
                },
                additional: Array.isArray(vendor.contactNumbers.additional) 
                    ? [...vendor.contactNumbers.additional] 
                    : []
            };
        } else if (vendor.contactNumber) {
            // Old schema - convert to new format
            initialFormData.contactNumbers = {
                primary: {
                    formatted: typeof vendor.contactNumber === 'object' 
                        ? vendor.contactNumber.formatted || '' 
                        : formatPhoneNumber(vendor.contactNumber || ''),
                    e164: typeof vendor.contactNumber === 'object' 
                        ? vendor.contactNumber.e164 || '' 
                        : formatE164(vendor.contactNumber || '')
                },
                additional: []
            };
        } else {
            // Fallback with empty data
            initialFormData.contactNumbers = {
                primary: { formatted: '', e164: '' },
                additional: []
            };
        }

        // Ensure we have at least 2 empty slots for additional numbers
        while (initialFormData.contactNumbers.additional.length < 2) {
            initialFormData.contactNumbers.additional.push({ formatted: '', e164: '' });
        }

        setFormData(initialFormData);
        setShowModal(true);
    };

    const formatPhoneNumber = (value) => {
        if (!value) return '';
        const cleaned = value.replace(/\D/g, '');
        const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
        if (!match) return value;
        return [
            match[1] && `(${match[1]}`,
            match[2] && `${match[1] ? ') ' : ''}${match[2]}`,
            match[3] && `-${match[3]}`,
        ]
            .filter(Boolean)
            .join('');
    };

    const formatE164 = (value) => {
        if (!value) return '';
        const cleaned = value.replace(/\D/g, '');
        return cleaned.length === 10 ? `+1${cleaned}` : '';
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleChange = (e) => {
        // Stop propagation to prevent sidebar from closing
        stopPropagation(e);
        
        const { name, value } = e.target;
        
        if (name === 'primaryPhone') {
            const cleaned = value.replace(/\D/g, '');
            setFormData(prev => ({
                ...prev,
                contactNumbers: {
                    ...prev.contactNumbers,
                    primary: {
                        formatted: formatPhoneNumber(value),
                        e164: formatE164(cleaned)
                    }
                }
            }));
        } else if (name.startsWith('additionalPhone')) {
            const index = parseInt(name.split('-')[1]);
            const cleaned = value.replace(/\D/g, '');
            
            setFormData(prev => {
                const newAdditional = [...prev.contactNumbers.additional];
                newAdditional[index] = {
                    formatted: formatPhoneNumber(value),
                    e164: formatE164(cleaned)
                };
                
                return {
                    ...prev,
                    contactNumbers: {
                        ...prev.contactNumbers,
                        additional: newAdditional
                    }
                };
            });
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleUpdate = (e) => {
        // Stop propagation to prevent sidebar from closing
        stopPropagation(e);
        
        // Validate primary phone
        const primaryPhone = formData.contactNumbers.primary.formatted.replace(/\D/g, '');
        if (primaryPhone.length !== 10) {
            setAlert({ message: 'Primary phone number must be 10 digits.', variant: 'danger' });
            return;
        }
    
        if (!validateEmail(formData.email)) {
            setAlert({ message: 'Invalid email address.', variant: 'danger' });
            return;
        }
        
        // Filter out empty additional phone numbers
        const filteredAdditional = formData.contactNumbers.additional.filter(phone => {
            const cleaned = phone.formatted.replace(/\D/g, '');
            return cleaned.length === 10;
        });
        
        // Check for duplicate phone numbers
        const allPhoneNumbers = [primaryPhone, ...filteredAdditional.map(p => p.formatted.replace(/\D/g, ''))];
        const uniquePhoneNumbers = [...new Set(allPhoneNumbers)];
        
        if (uniquePhoneNumbers.length !== allPhoneNumbers.length) {
            setAlert({ message: 'Duplicate phone numbers are not allowed.', variant: 'danger' });
            return;
        }
    
        try {
            // Prepare data for update
            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                contactNumbers: {
                    primary: formData.contactNumbers.primary,
                    additional: filteredAdditional
                }
                // Removed notificationPreferences
            };
            
            // Use the api instance to update profile
            api.put('/vendors/profile', updateData)
                .then(response => {
                    setVendor(response.data.data);
                    setAlert({ message: 'Details updated successfully!', variant: 'success' });
                    setShowModal(false);
                })
                .catch(error => {
                    console.error('Error updating vendor details:', error);
                    setAlert({ 
                        message: error.response?.data?.message || 'Failed to update details.', 
                        variant: 'danger' 
                    });
                });
        } catch (error) {
            console.error('Error updating vendor details:', error);
            setAlert({ 
                message: error.response?.data?.message || 'Failed to update details.', 
                variant: 'danger' 
            });
        }
    };

    // Get count of additional valid phone numbers
    const getAdditionalPhoneCount = () => {
        if (!vendor.contactNumbers?.additional) return 0;
        return vendor.contactNumbers.additional.filter(phone => phone.e164).length;
    };

    const displayPhoneNumbers = () => {
        if (vendor.contactNumbers?.primary) {
            // New schema with multiple numbers
            return (
                <>
                    <div className="mb-2">
                        <i className="bi bi-telephone me-2"></i>
                        {vendor.contactNumbers.primary.formatted || 'N/A'} 
                        <Badge bg="primary" className="ms-2">Primary</Badge>
                    </div>
                    
                    {vendor.contactNumbers.additional?.map((phone, index) => (
                        phone.formatted && (
                            <div className="mb-2" key={`phone-${index}`}>
                                <i className="bi bi-telephone me-2"></i>
                                {phone.formatted || 'N/A'}
                            </div>
                        )
                    ))}
                    
                </>
            );
        } else if (vendor.contactNumber) {
            // Old schema with single number
            return (
                <div className="mb-2">
                    <i className="bi bi-telephone me-2"></i>
                    {typeof vendor.contactNumber === 'object' 
                        ? vendor.contactNumber.formatted || 'N/A' 
                        : formatPhoneNumber(vendor.contactNumber) || 'N/A'}
                </div>
            );
        }
        
        return (
            <div className="mb-2">
                <i className="bi bi-telephone me-2"></i>
                N/A
            </div>
        );
    };
    
    // Handler to close modal without propagating the event
    const handleCloseModal = (e) => {
        stopPropagation(e);
        setShowModal(false);
    };

    return (
        <div className="container py-4" onClick={stopPropagation}>
            {alert.message && (
                <Alert 
                    variant={alert.variant}
                    className="mb-4"
                    style={{ maxWidth: '500px', margin: '0 auto' }}
                >
                    {alert.message}
                </Alert>
            )}

            <Card 
                className="shadow-sm" 
                style={{ 
                    maxWidth: '500px', 
                    margin: '0 auto',
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    border: '1px solid #e0e0e0'
                }}
                onClick={stopPropagation}
            >
                <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start">
                        <div>
                            <Card.Title className="mb-3" style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                                {vendor.firstName || 'N/A'} {vendor.lastName || 'N/A'}
                            </Card.Title>
                            <Card.Text style={{ fontSize: '1rem', color: '#555' }}>
                                {displayPhoneNumbers()}
                                
                                <div>
                                    <i className="bi bi-envelope me-2"></i>
                                    {vendor.email || 'N/A'}
                                </div>
                            </Card.Text>
                        </div>
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={handleEdit}
                            style={{
                                borderRadius: '8px',
                                padding: '0.5rem 1rem'
                            }}
                        >
                            Edit
                        </Button>
                    </div>
                </Card.Body>
            </Card>

            <Modal 
                show={showModal} 
                onHide={handleCloseModal} 
                centered
                size="md"
                onClick={stopPropagation}
                backdrop="static" // Prevents closing when clicking outside
                keyboard={false} // Prevents closing with ESC key
            >
                <Modal.Header 
                    closeButton 
                    style={{ borderBottom: '1px solid #e0e0e0' }}
                    onClick={stopPropagation}
                >
                    <Modal.Title>Edit Vendor Information</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" onClick={stopPropagation}>
                    <Form onClick={stopPropagation}>
                        <div className="row">
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>First Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="firstName"
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        onClick={stopPropagation}
                                        onFocus={stopPropagation}
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Group>
                            </div>
                            <div className="col-md-6">
                                <Form.Group className="mb-3">
                                    <Form.Label>Last Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="lastName"
                                        value={formData.lastName}
                                        onChange={handleChange}
                                        onClick={stopPropagation}
                                        onFocus={stopPropagation}
                                        style={{ borderRadius: '8px' }}
                                    />
                                </Form.Group>
                            </div>
                        </div>
                        
                        {/* Contact Numbers Section */}
                        <div className="contact-numbers mb-3" onClick={stopPropagation}>
                            <h5 className="mb-3">Contact Numbers</h5>
                            
                            {/* Primary Phone */}
                            <Form.Group className="mb-3">
                                <Form.Label>
                                    <strong>Primary Phone Number</strong> <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    type="text"
                                    name="primaryPhone"
                                    value={formData.contactNumbers.primary.formatted}
                                    onChange={handleChange}
                                    onClick={stopPropagation}
                                    onFocus={stopPropagation}
                                    maxLength="14"
                                    style={{ borderRadius: '8px' }}
                                    required
                                />
                                <Form.Text className="text-muted">
                                    This number will receive order notifications
                                </Form.Text>
                            </Form.Group>
                            
                            {/* Additional Phones */}
                            <div className="card p-3 mb-3" style={{ backgroundColor: '#f8f9fa' }} onClick={stopPropagation}>
                                <div className="card-body p-0" onClick={stopPropagation}>
                                    <h6 className="mb-3">Additional Phone Numbers (Optional)</h6>
                                    {formData.contactNumbers.additional.map((phone, index) => (
                                        <Form.Group className="mb-3" key={`phone-${index}`}>
                                            <Form.Label>Additional Phone #{index + 1}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name={`additionalPhone-${index}`}
                                                value={phone.formatted}
                                                onChange={handleChange}
                                                onClick={stopPropagation}
                                                onFocus={stopPropagation}
                                                maxLength="14"
                                                style={{ borderRadius: '8px' }}
                                            />
                                        </Form.Group>
                                    ))}
                                    
                                    <div className="alert alert-info mb-0" onClick={stopPropagation}>
                                        <i className="bi bi-info-circle me-2"></i>
                                        All saved phone numbers will receive order notifications
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <Form.Group className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                onClick={stopPropagation}
                                onFocus={stopPropagation}
                                style={{ borderRadius: '8px' }}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer 
                    style={{ borderTop: '1px solid #e0e0e0' }}
                    onClick={stopPropagation}
                >
                    <Button 
                        variant="outline-secondary" 
                        onClick={handleCloseModal}
                        style={{ borderRadius: '8px' }}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={handleUpdate}
                        style={{ borderRadius: '8px' }}
                    >
                        Update
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default VendorInfoCard;